import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { isCollaborator } from 'shared/store/selectors/auth.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getUserId } from 'shared/store/selectors/user.selector';
import { getUsers } from 'shared/store/selectors/contact.selector';
import { useEventsList, useScheduleEventDetail } from 'shared/hooks/schedule-events.hooks';
import monitorService from 'src/helpers/monitor.service';
import { isUpcomingEvent, EVENT_PARAM } from './helpers/event-view.helpers';
import { eventsKeys } from './helpers/query-keys';

const EducationalEventsContext = createContext(null);

export const useEducationalEventsContext = () => useContext(EducationalEventsContext);

export const HOSTED_BY = { ALL: 'ALL', MINE: 'MINE' };
export const DATES = { ALL: 'ALL', UPCOMING: 'UPCOMING' };
export const FORM_MODE = { CREATE: 'create', EDIT: 'edit' };

export function EducationalEventsProvider({ children }) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const schedulesLang = useSelector(getLang('SCHEDULES'));
  const userId = useSelector(getUserId);
  const users = useSelector(getUsers);
  const canManageEvents = useSelector(isCollaborator);
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedEventId = searchParams.get(EVENT_PARAM) || null;

  const setSelectedEventId = useCallback(
    (eventId, { replace = false } = {}) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (eventId) next.set(EVENT_PARAM, eventId);
          else next.delete(EVENT_PARAM);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  const [formMode, setFormMode] = useState(null);
  const [formEvent, setFormEvent] = useState(null);

  const {
    events,
    loading: loadingEvents,
    error: eventsError,
    loadMoreError: loadMoreEventsError,
    hasMore: hasMoreEvents,
    loadingMore: loadingMoreEvents,
    loadMore: loadMoreEvents,
  } = useEventsList();

  // The shared hook can't log (no platform logger in shared/) — surface both
  // failure paths to monitoring here.
  useEffect(() => {
    if (eventsError) {
      monitorService.error('Failed to load educational events list', { error: eventsError });
    }
  }, [eventsError]);
  useEffect(() => {
    if (loadMoreEventsError) {
      monitorService.error('Failed to load more educational events', {
        error: loadMoreEventsError,
      });
    }
  }, [loadMoreEventsError]);

  // The URL is the source of truth for the selection; fall back to the first
  // upcoming event when the param is absent or stale (deleted id). While more
  // future pages exist, an id missing from the list may just not be loaded yet
  // — keep it (the detail query fetches by id) instead of rewriting the URL.
  const selectedEventId = useMemo(() => {
    if (
      requestedEventId &&
      (hasMoreEvents || events.some(event => event.id === requestedEventId))
    ) {
      return requestedEventId;
    }
    return events.find(isUpcomingEvent)?.id ?? null;
  }, [requestedEventId, events, hasMoreEvents]);

  // Write the resolved default back to the URL so the selection is always a real
  // param; replace so it adds no history and self-heals a stale/absent id.
  useEffect(() => {
    if (selectedEventId && selectedEventId !== requestedEventId) {
      setSelectedEventId(selectedEventId, { replace: true });
    }
  }, [selectedEventId, requestedEventId, setSelectedEventId]);

  const detailQuery = useScheduleEventDetail(selectedEventId);
  const selectedEvent = detailQuery.data ?? null;

  // An id kept on trust (unloaded pages) that turns out dead — the detail
  // fetch errored — falls back to the default selection like a stale id.
  useEffect(() => {
    if (detailQuery.isError && requestedEventId) {
      setSelectedEventId(null, { replace: true });
    }
  }, [detailQuery.isError, requestedEventId, setSelectedEventId]);

  const refetchEvents = useCallback(
    () => queryClient.invalidateQueries({ queryKey: eventsKeys.list() }),
    [queryClient],
  );

  const refreshSelectedEvent = useCallback(() => {
    if (!selectedEventId) return;
    queryClient.invalidateQueries({ queryKey: eventsKeys.detail(selectedEventId) });
    queryClient.invalidateQueries({ queryKey: eventsKeys.checkInSession(selectedEventId) });
    queryClient.invalidateQueries({ queryKey: eventsKeys.checkIns(selectedEventId) });
  }, [queryClient, selectedEventId]);

  const openCreateForm = useCallback(() => {
    setFormEvent(null);
    setFormMode(FORM_MODE.CREATE);
  }, []);

  const openEditForm = useCallback(event => {
    setFormEvent(event);
    setFormMode(FORM_MODE.EDIT);
  }, []);

  const closeForm = useCallback(() => {
    setFormMode(null);
    setFormEvent(null);
  }, []);

  const value = useMemo(
    () => ({
      lang,
      schedulesLang,
      userId,
      users,
      canManageEvents,
      loading: loadingEvents,
      error: eventsError,
      events,
      hasMoreEvents,
      loadingMoreEvents,
      loadMoreEvents,
      loadMoreEventsError,
      selectedEventId,
      setSelectedEventId,
      selectedEvent,
      loadingDetail: detailQuery.isPending && !!selectedEventId,
      errorDetail: detailQuery.isError ? detailQuery.error : null,
      refetchEvents,
      refreshSelectedEvent,
      formMode,
      formEvent,
      openCreateForm,
      openEditForm,
      closeForm,
    }),
    [
      lang,
      schedulesLang,
      userId,
      users,
      canManageEvents,
      loadingEvents,
      eventsError,
      events,
      hasMoreEvents,
      loadingMoreEvents,
      loadMoreEvents,
      loadMoreEventsError,
      selectedEventId,
      setSelectedEventId,
      selectedEvent,
      detailQuery.isPending,
      detailQuery.isError,
      detailQuery.error,
      refetchEvents,
      refreshSelectedEvent,
      formMode,
      formEvent,
      openCreateForm,
      openEditForm,
      closeForm,
    ],
  );

  return (
    <EducationalEventsContext.Provider value={value}>{children}</EducationalEventsContext.Provider>
  );
}
